import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, throwError } from 'rxjs';
import { PoliticaEntregaApiService } from '../services/politica-entrega-api.service';
import { PoliticaEntregaMapper } from '../pages/politicas-entrega/mappers/politica-entrega.mapper';
import {
  POLITICAS_ENTREGA_INCOTERMS,
  PoliticaEntregaIncoterm,
  PoliticaEntregaSaveCommand,
  RegionPolitica,
} from '../models/politica-entrega.model';
import { PoliticaEntregaExcelImportResponseDto } from '../models/politica-entrega.dto';
import { PoliticaEntregaRowVm } from '../models/politica-entrega.vm';

@Injectable()
export class PoliticasEntregaStore {
  private readonly api = inject(PoliticaEntregaApiService);

  private readonly loadingDepth = signal(0);

  readonly regiones = signal<RegionPolitica[]>([]);
  readonly rows = signal<PoliticaEntregaRowVm[]>([]);
  readonly regionSeleccionada = signal<RegionPolitica | null>(null);
  readonly incotermsSeleccionado = signal<PoliticaEntregaIncoterm | null>(null);
  readonly error = signal<string | null>(null);
  readonly loading = computed(() => this.loadingDepth() > 0);
  readonly regionPreferida = computed(
    () => this.regiones().find((region) => region.codigo === 13) ?? this.regiones()[0] ?? null,
  );
  readonly incotermPreferido = computed<PoliticaEntregaIncoterm>(() => POLITICAS_ENTREGA_INCOTERMS[0]);
  readonly regionActiva = computed(() => this.regionSeleccionada() ?? this.regionPreferida());
  readonly incotermActivo = computed(() => this.incotermsSeleccionado() ?? this.incotermPreferido());

  loadRegiones(): Observable<void> {
    return this.execute(
      this.api.getRegiones().pipe(
        map((regions) => regions.map((region) => PoliticaEntregaMapper.toRegion(region))),
        map((regions) => {
          this.regiones.set(regions);
          const selectedCode = this.regionSeleccionada()?.codigo;
          const selected = selectedCode != null
            ? regions.find((region) => region.codigo === selectedCode) ?? null
            : null;
          this.regionSeleccionada.set(selected);
          if (regions.length === 0) {
            this.rows.set([]);
          }
        }),
        catchError((error) => {
          this.regiones.set([]);
          this.regionSeleccionada.set(null);
          this.rows.set([]);
          this.error.set('No se pudieron cargar las regiones.');
          return throwError(() => error);
        }),
      ),
    );
  }

  buscarPoliticas(): Observable<void> {
    const region = this.regionActiva();
    const incoterm = this.incotermActivo();
    if (!region) {
      return this.idleResult();
    }

    return this.execute(
      this.api.getPoliticas(region.codigo, incoterm).pipe(
        map((rows) => {
          this.rows.set(rows);
          this.error.set(null);
        }),
        catchError((error) => {
          this.rows.set([]);
          this.error.set('No se pudieron cargar las políticas de entrega.');
          return throwError(() => error);
        }),
      ),
    );
  }

  guardarCelda(command: Omit<PoliticaEntregaSaveCommand, 'regionCodigo' | 'incoterms'>): Observable<void> {
    const region = this.regionActiva();
    const incoterm = this.incotermActivo();
    if (!region) {
      return this.idleResult();
    }

    return this.execute(
      this.api.createUpdatePolitica({
        ...command,
        regionCodigo: region.codigo,
        incoterms: incoterm,
      }),
    );
  }

  eliminarCelda(id: number): Observable<void> {
    return this.execute(this.api.deletePoliticaById(id));
  }

  subirExcel(file: File): Observable<PoliticaEntregaExcelImportResponseDto> {
    return this.execute(this.api.postExcelPolitica(file));
  }

  setRegionSeleccionada(region: RegionPolitica | null): void {
    this.regionSeleccionada.set(region);
  }

  setIncotermsSeleccionado(value: string | null): void {
    if (!value) {
      this.incotermsSeleccionado.set(null);
      return;
    }
    const selected = POLITICAS_ENTREGA_INCOTERMS.find((item) => item === value);
    this.incotermsSeleccionado.set(selected ?? null);
  }

  clearError(): void {
    this.error.set(null);
  }

  private execute<T>(source$: Observable<T>): Observable<T> {
    this.beginLoading();
    return source$.pipe(finalize(() => this.endLoading()));
  }

  private idleResult(): Observable<void> {
    return of(undefined);
  }

  private beginLoading(): void {
    this.loadingDepth.update((depth) => depth + 1);
  }

  private endLoading(): void {
    this.loadingDepth.update((depth) => Math.max(0, depth - 1));
  }
}
